import React, { useContext, useRef, useEffect, FC, useCallback } from 'react';
import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { isBrowser } from '@agria/utils';
import { Blades, UiStateContext } from '../../context/UiStateContext';
import { Portal } from '../portal/Portal';
import { Typography } from '../typography/Typography';
import { Close } from '../icon';
import { Divider } from '../divider/Divider';
import { Stack } from '../stack/Stack';
import tokens from './tokens';

export type BladeWidths = 'narrow' | 'wide' | 'full';

export interface BladeProps {
  id: Blades;
  width?: BladeWidths;
  renderChildrenOnOpen?: boolean;
  heading?: string;
  customBlade?: boolean;
  secondary?: boolean;
}

interface BladeContainerProps {
  heading?: string;
  secondary?: boolean;
}

interface StyledBladeProps {
  isOpen?: boolean;
  width?: BladeWidths;
  animate?: boolean;
}

const styleSettings = {
  narrowContainer: '548px',
  wideContainer: `${tokens.wideWidth}`,
  fullContainer: '1292px',
  overlay: {
    transitionSpeed: '0.25s',
    transitionDelay: '0s',
  },
  container: {
    transitionSpeed: '0.5s',
    transitionDelay: '0.25s',
  },
};

const StyledBladeContainer = styled.div`
  ${({ theme: { colors, shadows, maxMedia } }) => css`
    display: flex;
    flex-direction: column;
    position: absolute;
    max-width: var(--containerWidth);
    transform: translateX(var(--containerOffset));
    right: 0;
    top: 0;
    width: 100vw;
    width: 100dvw;
    height: 100vh;
    height: 100dvh;
    max-height: -webkit-fill-available;
    background-color: ${colors.utilityWhite};
    box-shadow: ${shadows.medium};

    @media (max-width: ${maxMedia.small}) {
      max-width: 100%;
    }
  `};
`;

const StyledBlade = styled.div<StyledBladeProps>`
  ${({ theme: { layers }, isOpen, width, animate, secondary }) => {
    const slideIn = keyframes`
      from {
        transform: translateX(var(--containerOffset));
      }
      to {
        transform: translateX(0);
      }
    `;

    const slideOut = keyframes`
      from {
        transform: translateX(0);
      }
      to {
        transform: translateX(var(--containerOffset));
      }
    `;

    const fadeIn = keyframes`
      from {
        opacity: 0;
        display: none;
      }
      to {
        opacity: 1;
        display: block;
      }
    `;

    const fadeOut = keyframes`
      from {
        opacity: 1;
        display: block;
      }
      to {
        opacity: 0;
        display: none;
      }
    `;

    let containerWidth = styleSettings.narrowContainer;
    if (width === 'wide') {
      containerWidth = styleSettings.wideContainer;
    }
    if (width === 'full') {
      containerWidth = styleSettings.fullContainer;
    }

    return css`
      --containerWidth: ${containerWidth};
      --containerOffset: ${containerWidth};
      display: ${isOpen ? 'block' : 'none'};
      position: fixed;
      right: 0;
      top: 0;
      width: 100dvw;
      height: 100dvh;
      max-height: -webkit-fill-available;
      z-index: ${secondary ? layers.secondaryBlade : layers.blade};
      animation: ${isOpen ? fadeIn : fadeOut}
        ${styleSettings.overlay.transitionSpeed}
        ${styleSettings.overlay.transitionDelay} 1 normal forwards;

      ${StyledBladeContainer} {
        animation: ${isOpen ? slideIn : slideOut}
          ${styleSettings.container.transitionSpeed} ease-out
          ${styleSettings.container.transitionDelay} 1 normal forwards;
      }
    `;
  }};
`;

const StyledBladeOverlay = styled.button`
  ${({ theme: { colors } }) => css`
    background-color: rgba(0, 0, 0, 0.5);
    position: absolute;
    left: 0;
    top: 0;
    width: 100dvw;
    height: 100dvh;
    border: none;
  `};
`;

const StyledTopClose = styled.button`
  ${({ theme: { space, colors } }) => css`
    background: none;
    border: none;
    padding: ${space.small};
    cursor: pointer;
    position: absolute;
    background: ${colors.brandOne100};
    clip-path: polygon(0 0, 100% 0, 100% 100%);
    display: flex;
    justify-content: flex-end;
    height: 100%;
    inset: 0 0 auto auto;
    max-height: 72px;
    max-width: 72px;
    padding: 4px;
    position: absolute;
    width: 100%;
    z-index: 1;

    svg {
      position: relative;
    }
  `};
`;

const StyledBladeFooter = styled.button`
  ${({ theme: { space, colors } }) => css`
    display: flex;
    justify-content: center;
    padding: ${space.large};
    background-color: ${colors.utilityBackground};
    text-decoration: underline;
  `};
`;

export const StyledBladeBody = styled.div`
  ${({ theme: { space } }) => css`
    padding-top: ${space.xxxLarge};
    padding-bottom: ${space.xxxLarge};
    padding-left: ${space.fluidMediumToXxxLarge};
    padding-right: ${space.fluidMediumToXxxLarge};
    flex-grow: 1;
    overflow-y: auto;
  `};
`;

export const BladeContainer: FC<
  React.PropsWithChildren<BladeContainerProps>
> = ({ children, heading, secondary }) => {
  const {
    setActiveBlade,
    setActiveSecondaryBlade,
    activeBladeTriggerEl,
    setActiveBladeTriggerEl,
    setActiveSecondaryBladeTriggerEl,
    activeBladeWidth,
    setActiveBladeWidth,
  } = useContext(UiStateContext);

  const handleClose = () => {
    if (!secondary) {
      setActiveBlade(null);
    }
    setActiveSecondaryBlade(null);

    if (activeBladeWidth) {
      setActiveBladeWidth(undefined);
    }

    if (activeBladeTriggerEl?.current) {
      activeBladeTriggerEl.current.focus();
      setActiveBladeTriggerEl(undefined);
      setActiveSecondaryBladeTriggerEl(undefined);
    }
  };

  return (
    <StyledBladeContainer>
      <StyledTopClose aria-label="close" onClick={handleClose}>
        <Close color="utilityWhite" size="medium" />
      </StyledTopClose>
      <StyledBladeBody>
        <Stack gap={tokens.gap}>
          {heading && (
            <Stack gap={tokens.gap}>
              <Typography component="h3" variant="headingMedium">
                {heading}
              </Typography>
              <Divider />
            </Stack>
          )}
          <div>{children}</div>
        </Stack>
      </StyledBladeBody>
      <StyledBladeFooter onClick={handleClose}>Close</StyledBladeFooter>
    </StyledBladeContainer>
  );
};

const Blade: FC<React.PropsWithChildren<BladeProps>> = ({
  children,
  customBlade = false,
  heading,
  id,
  renderChildrenOnOpen = false,
  width = 'narrow',
  secondary,
}) => {
  const {
    activeBlade,
    setActiveBlade,
    activeBladeTriggerEl,
    setActiveBladeTriggerEl,
    activeSecondaryBladeTriggerEl,
    setActiveSecondaryBladeTriggerEl,
    activeBladeWidth,
    setActiveBladeWidth,
    activeBladeAnimate,
    activeSecondaryBlade,
    setActiveSecondaryBlade,
  } = useContext(UiStateContext);

  const bladeEl = useRef<HTMLDivElement | null | undefined>();

  const focusableElements =
    'a[href], button:not(#blade-overlay), textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select';

  const isActive =
    id === activeBlade || (secondary && id === activeSecondaryBlade);

  const handleClose = useCallback(() => {
    if (!secondary) {
      setActiveBlade(null);
    }
    setActiveSecondaryBlade(null);

    if (activeBladeWidth) {
      setActiveBladeWidth(undefined);
    }

    if (activeBladeTriggerEl?.current) {
      activeBladeTriggerEl.current.focus();
      setActiveBladeTriggerEl(undefined);
      setActiveSecondaryBladeTriggerEl(undefined);
    }
  }, [
    activeBladeTriggerEl,
    activeBladeWidth,
    setActiveBlade,
    setActiveBladeTriggerEl,
    setActiveBladeWidth,
    setActiveSecondaryBlade,
    setActiveSecondaryBladeTriggerEl,
  ]);

  const handleTabKey = (e) => {
    const focusableBladeElements =
      bladeEl?.current?.querySelectorAll<HTMLElement>(focusableElements);
    const firstElement = focusableBladeElements
      ? focusableBladeElements[0]
      : undefined;
    const lastElement = focusableBladeElements
      ? focusableBladeElements[focusableBladeElements.length - 1]
      : undefined;

    if (firstElement && !e.shiftKey && document.activeElement === lastElement) {
      firstElement.focus();
      e.preventDefault();
    }

    if (lastElement && e.shiftKey && document.activeElement === firstElement) {
      lastElement.focus();
      e.preventDefault();
    }
  };

  useEffect(() => {
    const focusableBladeElements =
      bladeEl?.current?.querySelectorAll<HTMLElement>(focusableElements);
    const { body } = document;
    const scrollbarWidth = window.innerWidth - body.clientWidth;
    if (focusableBladeElements && focusableBladeElements.length >= 1) {
      const firstElement = focusableBladeElements[0];
      firstElement?.focus();
    }
    const keyListenersMap = new Map([
      [27, handleClose],
      [9, handleTabKey],
    ]);

    const keyListener = (e) => {
      const listener = keyListenersMap.get(e.keyCode as number);
      return listener && listener(e);
    };

    if (isActive) {
      if (isBrowser) {
        body.style.paddingRight = `${scrollbarWidth}px`;
        body.style.overflow = 'hidden';
        window.addEventListener('keydown', keyListener);
      }
    }
    return () => {
      if (isBrowser) {
        body.style.paddingRight = '';
        body.style.overflow = '';
        window.removeEventListener('keydown', keyListener);
      }
    };
  }, [isActive, id, handleClose]);

  return (
    <Portal id="blade-portal">
      <StyledBlade
        isOpen={isActive}
        role="dialog"
        aria-modal="true"
        aria-hidden={activeBlade !== id}
        ref={bladeEl as any}
        id={id as string}
        width={activeBladeWidth || width}
        animate={activeBladeAnimate}
        secondary={secondary}
      >
        <StyledBladeOverlay
          aria-label="Close blade"
          onClick={handleClose}
          tabIndex={-1}
          id="blade-overlay"
        />
        {customBlade ? (
          children
        ) : (
          <BladeContainer heading={heading} secondary={secondary}>
            {renderChildrenOnOpen ? isActive && children : children}
          </BladeContainer>
        )}
      </StyledBlade>
    </Portal>
  );
};

export { Blade };
